import { logWarn } from "./log";
export class StaticEngine {
  start(volume) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return false;
    this.ctx = new Ctx();
    const len = this.ctx.sampleRate * 2;
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.src = this.ctx.createBufferSource();
    this.src.buffer = buf;
    this.src.loop = true;
    this.filter = this.ctx.createBiquadFilter();
    this.filter.type = "bandpass";
    this.filter.Q.value = 0.6;
    this.filter.frequency.value = 700;
    this.bass = this.ctx.createBiquadFilter();
    this.bass.type = "lowshelf";
    this.bass.frequency.value = 260;
    this.bass.gain.value = 9;
    this.chop = this.ctx.createGain();
    this.master = this.ctx.createGain();
    this.master.gain.value = volume;
    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 1024;
    this.src.connect(this.filter).connect(this.bass).connect(this.chop).connect(this.master).connect(this.analyser);
    this.analyser.connect(this.ctx.destination);
    this.src.start();
    return true;
  }

  tick(mhz) {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const f = 180 + ((mhz - 87.5) / 20.5) * 1400 + Math.random() * 350;
    this.filter.frequency.setTargetAtTime(f, t, 0.01);
    this.chop.gain.setValueAtTime(Math.random() < 0.3 ? 0.04 : 0.5 + Math.random() * 0.8, t);
  }

  muffle(audioEl) {
    if (!this.ctx) return null;
    const src = this.ctx.createMediaElementSource(audioEl);
    const lp = this.ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 650;
    lp.Q.value = 0.9;
    const hp = this.ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 180;
    src.connect(hp).connect(lp).connect(this.master);
    return lp;
  }

  duck(on) {
    if (!this.ctx) return;
    this.chop.gain.setTargetAtTime(on ? 0.12 : 0.8, this.ctx.currentTime, 0.05);
  }

  setVolume(v) {
    if (this.master) this.master.gain.value = v;
  }

  stop() {
    try { this.src?.stop(); this.ctx?.close(); } catch (e) { logWarn("Arresto audio non riuscito:", e); }
    this.ctx = null;
    this.analyser = null;
  }
}

const SILENCE = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
const NEAR = 1.2;
const FAR = 1.6;
const DEEP_RATE = 0.82;
const RADIO_LEVEL = 0.35;
const AUDIBLE_CHANCE = 0.55;
const RADIO_WINDOW = 0.1;

const isAudible = (p, d, muted) => !muted && p.live && p.lucky && d <= RADIO_WINDOW;

export class RadioBank {
  start(stations, engine) {
    this.players = stations.map((s, i) => {
      const a = new Audio(SILENCE);
      a.volume = 0;
      a.play().catch((e) => logWarn("Sblocco audio non riuscito:", e));
      const out = engine?.muffle(a);
      return { ...s, url: `${window.location.origin}/api/stream/${i}`, audio: a, live: false, lucky: false, out };
    });
  }

  wake(p) {
    p.live = true;
    p.lucky = Math.random() < AUDIBLE_CHANCE;
    p.audio.src = p.url;
    p.audio.preservesPitch = false;
    p.audio.mozPreservesPitch = false;
    p.audio.webkitPreservesPitch = false;
    p.audio.play().then(() => { p.audio.playbackRate = DEEP_RATE; }).catch((e) => logWarn(`Stazione ${p.name} non disponibile:`, e));
  }

  sleep(p) {
    p.live = false;
    p.audio.pause();
    p.audio.src = SILENCE;
  }

  keepAlive(p, d) {
    if (d <= NEAR && !p.live) this.wake(p);
    if (d > FAR && p.live) this.sleep(p);
  }

  tune(mhz, volume, muted) {
    const level = Math.min(1, volume * RADIO_LEVEL);
    let hit = null;
    for (const p of this.players || []) {
      const d = Math.abs(p.freq - mhz);
      this.keepAlive(p, d);
      const on = isAudible(p, d, muted);
      p.audio.volume = on ? level : 0;
      if (on) hit = p;
    }
    return hit;
  }

  stop() {
    (this.players || []).forEach((p) => { p.audio.pause(); p.audio.src = ""; });
    this.players = [];
  }
}

export const speak = (text, volume, onEnd, lang = "it-IT") => {
  if (!window.speechSynthesis) return onEnd?.();
  const u = new SpeechSynthesisUtterance(text.toLowerCase());
  u.lang = lang;
  u.pitch = 0.05 + Math.random() * 0.35;
  u.rate = 0.65 + Math.random() * 0.25;
  u.volume = Math.min(1, volume * 0.8);
  const it = window.speechSynthesis.getVoices().find((v) => v.lang?.startsWith(lang.slice(0, 2)));
  if (it) u.voice = it;
  const finish = once(onEnd);
  u.onend = finish;
  u.onerror = finish;
  setTimeout(finish, 4000);
  window.speechSynthesis.speak(u);
};

const MALE_HINTS = /male|uomo|luca|cosimo|diego|jorge|carlos|thomas|daniel|paul|alex|fred|reed|eddy|rocko|grandpa|markus|yannick|jacques|henri|joaquim|duarte|giorgio|paolo|enrique|david|mark|george|arthur|oliver/i;
const FEMALE_HINTS = /female|donna|alice|elsa|federica|paola|samantha|victoria|karen|moira|tessa|monica|paulina|amelie|anna|helena|joana|luciana|zira|susan|serena/i;

const maleVoice = (lang) => {
  const voices = window.speechSynthesis.getVoices().filter((v) => v.lang?.startsWith(lang.slice(0, 2)));
  return voices.find((v) => MALE_HINTS.test(v.name) && !FEMALE_HINTS.test(v.name))
    || voices.find((v) => !FEMALE_HINTS.test(v.name)) || voices[0];
};

const once = (fn) => {
  let done = false;
  return () => { if (!done) { done = true; fn?.(); } };
};

const RITUAL_PAUSE = 1100;
const RITUAL_MAX_MS = 40000;
let ritualId = 0;

export const cancelInvocation = () => { ritualId += 1; };

const sayGently = (text, volume, lang, onDone) => {
  const u = new SpeechSynthesisUtterance(text);
  u.lang = lang;
  u.pitch = 0.75;
  u.rate = 0.78;
  u.volume = Math.min(1, volume * 1.4 + 0.2);
  const v = maleVoice(lang);
  if (v) u.voice = v;
  const finish = once(onDone);
  u.onend = finish;
  u.onerror = finish;
  setTimeout(finish, Math.min(9000, 1500 + text.length * 80));
  window.speechSynthesis.speak(u);
};

export const speakInvocation = (text, volume, onEnd, lang = "it-IT") => {
  if (!window.speechSynthesis) return onEnd?.();
  const lines = text.split("|");
  const id = ++ritualId;
  const end = once(onEnd);
  setTimeout(() => { if (id === ritualId) { ritualId += 1; end(); } }, RITUAL_MAX_MS);
  const next = (i) => {
    if (id !== ritualId) return;
    if (i >= lines.length) { end(); return; }
    sayGently(lines[i], volume, lang, () => setTimeout(() => next(i + 1), RITUAL_PAUSE));
  };
  next(0);
  return undefined;
};
